import type { ApiResult } from '@bcube/shared';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { priceTableApi } from '@/api/commercial/price-tables';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';

interface PublishPriceTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceTableId?: string;
  onSuccess?: () => void;
  onValidationError?: (errors: { startDateTime?: string; endDateTime?: string }) => void;
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getPublishErrorMessage(error: ApiResult['error']): {
  toast: string;
  field?: string;
} {
  if (error?.code === 'CONFLICTING_DATE_RANGE') {
    const name = error.meta?.conflictingTableName ?? 'outra tabela';
    const start = error.meta?.conflictingStart ? formatDate(error.meta.conflictingStart) : '';
    const end = error.meta?.conflictingEnd ? formatDate(error.meta.conflictingEnd) : '';
    const period = start && end ? ` (${start} — ${end})` : '';

    return {
      toast: `Já existe uma tabela provisória "${name}" com datas que conflitam com o período informado${period}. Ajuste as datas de início e/ou fim para evitar sobreposição.`,
      field: `Período conflitante com a tabela "${name}"${period}`,
    };
  }

  return {
    toast: error?.global || 'Erro ao publicar tabela. Tente novamente.',
  };
}

export function PublishPriceTableDialog({
  open,
  onOpenChange,
  priceTableId,
  onSuccess,
  onValidationError,
}: PublishPriceTableDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handlePublish = async () => {
    if (!priceTableId) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'ID da tabela não encontrado.',
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await priceTableApi.publish(priceTableId);

      if (result.success) {
        toast({
          variant: 'success',
          title: 'Sucesso',
          description: 'Tabela publicada com sucesso!',
        });
        onOpenChange(false);
        onSuccess?.();
      } else {
        const { toast: toastMessage, field: fieldMessage } = getPublishErrorMessage(result.error);
        toast({
          variant: 'destructive',
          title: 'Erro ao publicar',
          description: toastMessage,
        });
        onOpenChange(false);

        if (result.error?.field && fieldMessage) {
          onValidationError?.({ [result.error.field]: fieldMessage });
        }
      }
    } catch (_error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao publicar tabela. Tente novamente.',
      });
    } finally {
      setIsLoading(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md gap-4 rounded-lg p-5 [&>button]:hidden">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden="true" />
          </div>
          <DialogHeader className="space-y-2 pb-0 text-left">
            <DialogTitle className="text-base leading-5">Publicar tabela!</DialogTitle>
            <DialogDescription className="text-xs leading-[18px] text-gray-500">
              Ao confirmar a ação, a tabela será publicada na data e hora informadas para início na
              área de configurações gerais. Se a tabela a ser publicada for do tipo normal,
              consequentemente se houver outra tabela normal ativa será automaticamente inativada.
              Também ao publicar uma tabela normal, se houver uma tabela provisória em vigor, então
              a tabela do tipo normal entrará automaticamente em pausa na data e hora informadas nas
              configurações gerais para publicação e será utilizada apenas quando a tabela
              provisória for inativada. Se a tabela a ser publicada for do tipo provisória e houver
              uma tabela do tipo normal ativa, esta última entrará automaticamente em pausa pelo
              período que a provisória estiver em vigor.
            </DialogDescription>
          </DialogHeader>
        </div>
        <DialogFooter className="flex-row justify-end gap-2 pt-0 sm:space-x-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={handlePublish}
            disabled={isLoading}
          >
            {isLoading ? 'Publicando...' : 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
